import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Loader2, LogOut, Upload, FileText, Image, Trash2, Download,
  RefreshCw, Clock, CheckCircle2, XCircle, ChevronDown, ChevronUp,
  Eye, AlertCircle
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/api";
import { errMsg } from "@/utils";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { analytics } from "@/services/analytics";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel,
  AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const DOCUMENT_TYPES = [
  { value: "passport",       label: "Passport" },
  { value: "pan_card",       label: "PAN Card" },
  { value: "aadhaar",        label: "Aadhaar Card" },
  { value: "address_proof",  label: "Address Proof" },
  { value: "bank_statement", label: "Bank Statement" },
  { value: "tax_document",   label: "Tax Document" },
  { value: "other",          label: "Other" },
];

const MAX_SIZE_MB = 10;
const ALLOWED_TYPES = ["image/jpeg", "image/png", "image/webp", "application/pdf"];
const ALLOWED_EXTS = [".jpg", ".jpeg", ".png", ".webp", ".pdf"];

const STATUS_CONFIG = {
  pending:  { label: "Pending Review", color: "bg-amber-50 text-amber-700 border-amber-200",  icon: Clock },
  approved: { label: "Approved",       color: "bg-emerald-50 text-emerald-700 border-emerald-200", icon: CheckCircle2 },
  rejected: { label: "Rejected",       color: "bg-red-50 text-red-600 border-red-200",        icon: XCircle },
};

export default function Documents() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const fileRef = useRef(null);

  const [docs, setDocs] = useState([]);
  const [loadingDocs, setLoadingDocs] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selectedType, setSelectedType] = useState("passport");
  const [replaceTargetId, setReplaceTargetId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [historyMap, setHistoryMap] = useState({});
  const [loadingHistory, setLoadingHistory] = useState({});

  useEffect(() => {
    if (!loading && !user) navigate("/login", { replace: true });
  }, [loading, user, navigate]);

  const loadDocs = useCallback(async () => {
    if (!user) return;
    setLoadingDocs(true);
    try {
      const { data } = await api.get("/kyc/documents");
      setDocs(data);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setLoadingDocs(false); }
  }, [user]);

  useEffect(() => { if (user) loadDocs(); }, [user, loadDocs]);

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!ALLOWED_TYPES.includes(file.type)) {
      toast.error("Allowed types: JPEG, PNG, WebP, PDF");
      e.target.value = "";
      return;
    }
    if (file.size > MAX_SIZE_MB * 1024 * 1024) {
      toast.error(`File must be under ${MAX_SIZE_MB} MB`);
      e.target.value = "";
      return;
    }
    uploadFile(file);
    e.target.value = "";
  };

  const uploadFile = (file) => {
    setUploading(true);
    const reader = new FileReader();
    reader.onload = async (ev) => {
      const [, base64] = ev.target.result.split(",");
      try {
        const endpoint = replaceTargetId
          ? `/kyc/documents/${replaceTargetId}`
          : "/kyc/documents";
        const method = replaceTargetId ? "put" : "post";
        await api[method](endpoint, {
          document_type: selectedType,
          file_name: file.name,
          mime_type: file.type,
          file_size: file.size,
          file_data: base64,
        });
        toast.success(replaceTargetId ? "Document replaced" : "Document uploaded");
        analytics.kycDocumentUpload(selectedType);
        setReplaceTargetId(null);
        loadDocs();
      } catch (err) { toast.error(errMsg(err)); }
      finally { setUploading(false); }
    };
    reader.onerror = () => { toast.error("Failed to read file"); setUploading(false); };
    reader.readAsDataURL(file);
  };

  const handleDelete = async (docId) => {
    try {
      await api.delete(`/kyc/documents/${docId}`);
      toast.success("Document removed");
      loadDocs();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const handleDownload = async (doc) => {
    try {
      const { data } = await api.get(`/kyc/documents/${doc.document_id}/download`, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = doc.file_name;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const loadHistory = async (docId) => {
    if (historyMap[docId]) {
      setExpandedId(expandedId === docId ? null : docId);
      return;
    }
    setLoadingHistory(p => ({ ...p, [docId]: true }));
    try {
      const { data } = await api.get(`/kyc/documents/${docId}/history`);
      setHistoryMap(p => ({ ...p, [docId]: data }));
      setExpandedId(docId);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setLoadingHistory(p => ({ ...p, [docId]: false })); }
  };

  if (loading || !user) {
    return <div className="min-h-screen grid place-items-center bg-[hsl(var(--zt-beige))]"><Loader2 className="animate-spin text-[hsl(var(--zt-teal))]" size={28} /></div>;
  }

  const uploadedTypes = new Set(docs.map(d => d.document_type));

  return (
    <div className="min-h-screen bg-[hsl(var(--zt-beige))] pb-24">
      {/* Header */}
      <div className="bg-[hsl(var(--zt-teal))] text-white relative overflow-hidden">
        <div aria-hidden className="absolute -top-40 -right-40 w-[450px] h-[450px] rounded-full opacity-[0.15] blur-3xl pointer-events-none"
          style={{ background: "radial-gradient(circle, #4F63F0 0%, transparent 60%)" }} />
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 h-24 flex items-center justify-between relative z-10">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate("/")} className="transition-transform hover:scale-[1.02]">
              <img src="/zenstrust-logo.png" alt="Zenstrust" className="h-14 w-auto rounded-md object-contain" />
            </button>
            <span className="hidden sm:inline font-heading uppercase tracking-[0.25em] text-[10px] text-[hsl(var(--zt-gold))] font-bold border-l border-white/15 pl-4">
              KYC Documents
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" onClick={() => navigate("/dashboard")}
              className="text-white hover:bg-white/10 font-heading uppercase tracking-[0.16em] text-xs h-10 px-4 rounded-full border border-white/10 hidden sm:flex">
              Dashboard
            </Button>
            <Button variant="ghost" onClick={async () => { await logout(); navigate("/"); }}
              className="text-white hover:bg-white/10 font-heading uppercase tracking-[0.16em] text-xs h-10 px-5 rounded-full border border-white/10">
              <LogOut size={15} className="mr-2" /> Log out
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-[1000px] mx-auto px-6 md:px-12 py-12 space-y-8">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="font-display text-3xl lg:text-4xl text-[hsl(var(--zt-teal))] font-bold">KYC Documents</h1>
            <p className="text-sm text-[hsl(var(--zt-charcoal))]/60 mt-1 font-body">
              Upload your identification and financial documents for verification.
            </p>
          </div>
          <div className="text-right text-sm font-heading uppercase tracking-wider text-[hsl(var(--zt-charcoal))]/50">
            <span className="text-[hsl(var(--zt-teal))] font-bold text-lg">{docs.filter(d => d.status === "approved").length}</span>
            <span className="text-[10px] block">of {DOCUMENT_TYPES.length} verified</span>
          </div>
        </div>

        {/* Upload area */}
        <div className="bg-white rounded-2xl border border-[hsl(var(--zt-beige-deep))] p-6 shadow-sm">
          <h2 className="font-heading uppercase tracking-[0.18em] text-[11px] text-[hsl(var(--zt-teal))]/70 font-bold mb-4">Upload Document</h2>
          <div className="flex flex-wrap gap-3 items-end">
            <div className="flex-1 min-w-[200px]">
              <label htmlFor="doc-type" className="block text-[10px] font-heading uppercase tracking-wider text-[hsl(var(--zt-charcoal))]/55 mb-1.5">Document Type</label>
              <select
                id="doc-type"
                value={selectedType}
                onChange={e => setSelectedType(e.target.value)}
                className="w-full h-10 px-3 text-sm border border-[hsl(var(--zt-beige-deep))] rounded-xl focus:outline-none focus:ring-2 focus:ring-[hsl(var(--zt-teal))]/20 bg-[hsl(var(--zt-beige))]/30"
              >
                {DOCUMENT_TYPES.map(t => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>
            <div>
              <p className="text-[10px] font-heading uppercase tracking-wider text-[hsl(var(--zt-charcoal))]/45 mb-1.5">
                JPEG, PNG, WebP or PDF · Max {MAX_SIZE_MB} MB
              </p>
              <Button
                onClick={() => { setReplaceTargetId(null); fileRef.current?.click(); }}
                disabled={uploading}
                className="bg-[hsl(var(--zt-gold))] text-white hover:bg-[hsl(var(--zt-gold))]/90 rounded-full h-10 px-6 font-heading uppercase tracking-wider text-xs font-bold"
              >
                {uploading ? <Loader2 size={14} className="animate-spin mr-2" /> : <Upload size={14} className="mr-2" />}
                {uploading ? "Uploading…" : "Select File"}
              </Button>
            </div>
          </div>
          <input ref={fileRef} type="file" accept={ALLOWED_EXTS.join(",")} className="hidden" onChange={handleFileSelect} />
        </div>

        {/* Document list */}
        {loadingDocs ? (
          <div className="space-y-3">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="bg-white rounded-2xl border border-[hsl(var(--zt-beige-deep))] p-5 h-20 animate-pulse" />
            ))}
          </div>
        ) : docs.length === 0 ? (
          <div className="bg-white border border-dashed border-[hsl(var(--zt-teal))]/20 rounded-3xl p-16 text-center">
            <FileText size={32} className="mx-auto mb-4 text-[hsl(var(--zt-teal))]/25" />
            <p className="font-heading text-[hsl(var(--zt-teal))] text-lg font-bold">No documents uploaded yet</p>
            <p className="text-sm text-[hsl(var(--zt-charcoal))]/50 mt-2">Upload your KYC documents above to get started.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {docs.map(doc => {
              const sc = STATUS_CONFIG[doc.status] || STATUS_CONFIG.pending;
              const StatusIcon = sc.icon;
              const isExpanded = expandedId === doc.document_id;
              const isPdf = doc.mime_type === "application/pdf";

              return (
                <div key={doc.document_id} className="bg-white rounded-2xl border border-[hsl(var(--zt-beige-deep))] shadow-sm overflow-hidden">
                  <div className="p-5 flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-xl grid place-items-center flex-shrink-0 ${isPdf ? "bg-red-50 border border-red-100" : "bg-blue-50 border border-blue-100"}`}>
                      {isPdf ? <FileText size={18} className="text-red-400" /> : <Image size={18} className="text-blue-400" />}
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-semibold text-sm text-[hsl(var(--zt-teal))]">
                          {DOCUMENT_TYPES.find(t => t.value === doc.document_type)?.label || doc.document_type}
                        </p>
                        <span className={`inline-flex items-center gap-1 text-[9px] font-heading uppercase tracking-wider px-2 py-0.5 rounded-full border font-bold ${sc.color}`}>
                          <StatusIcon size={9} />
                          {sc.label}
                        </span>
                      </div>
                      <p className="text-[11px] text-[hsl(var(--zt-charcoal))]/50 mt-0.5 truncate">{doc.file_name} · {formatSize(doc.file_size)}</p>
                      {doc.rejection_reason && (
                        <p className="text-[11px] text-red-600 mt-1 flex items-center gap-1">
                          <AlertCircle size={11} /> {doc.rejection_reason}
                        </p>
                      )}
                    </div>

                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      <button
                        onClick={() => handleDownload(doc)}
                        title="Download"
                        className="w-8 h-8 rounded-lg bg-[hsl(var(--zt-beige))] grid place-items-center text-[hsl(var(--zt-charcoal))]/50 hover:text-[hsl(var(--zt-teal))] hover:bg-[hsl(var(--zt-beige-deep))] transition-colors"
                      >
                        <Download size={13} />
                      </button>

                      {doc.status !== "approved" && (
                        <button
                          onClick={() => { setReplaceTargetId(doc.document_id); setSelectedType(doc.document_type); fileRef.current?.click(); }}
                          title="Replace"
                          className="w-8 h-8 rounded-lg bg-[hsl(var(--zt-beige))] grid place-items-center text-[hsl(var(--zt-charcoal))]/50 hover:text-[hsl(var(--zt-teal))] hover:bg-[hsl(var(--zt-beige-deep))] transition-colors"
                        >
                          <RefreshCw size={13} />
                        </button>
                      )}

                      <button
                        onClick={() => loadHistory(doc.document_id)}
                        title="History"
                        className="w-8 h-8 rounded-lg bg-[hsl(var(--zt-beige))] grid place-items-center text-[hsl(var(--zt-charcoal))]/50 hover:text-[hsl(var(--zt-teal))] hover:bg-[hsl(var(--zt-beige-deep))] transition-colors"
                      >
                        {loadingHistory[doc.document_id] ? (
                          <Loader2 size={13} className="animate-spin" />
                        ) : isExpanded ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
                      </button>

                      {doc.status !== "approved" && (
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <button
                              title="Delete"
                              className="w-8 h-8 rounded-lg bg-red-50 grid place-items-center text-red-400 hover:bg-red-100 transition-colors"
                            >
                              <Trash2 size={13} />
                            </button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete document?</AlertDialogTitle>
                              <AlertDialogDescription>
                                This will permanently remove the document. This cannot be undone.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction onClick={() => handleDelete(doc.document_id)} className="bg-red-500 hover:bg-red-600">
                                Delete
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      )}
                    </div>
                  </div>

                  {/* History panel */}
                  {isExpanded && historyMap[doc.document_id] && (
                    <div className="border-t border-[hsl(var(--zt-beige-deep))] bg-[hsl(var(--zt-beige))]/40 px-5 py-4">
                      <p className="text-[10px] font-heading uppercase tracking-wider text-[hsl(var(--zt-charcoal))]/50 mb-3 font-bold">Document History</p>
                      {historyMap[doc.document_id].length === 0 ? (
                        <p className="text-sm text-[hsl(var(--zt-charcoal))]/40">No history available</p>
                      ) : (
                        <ul className="space-y-2">
                          {historyMap[doc.document_id].map(h => (
                            <li key={h.id} className="flex items-start gap-3 text-sm">
                              <div className="w-1.5 h-1.5 rounded-full bg-[hsl(var(--zt-teal))]/40 mt-1.5 flex-shrink-0" />
                              <div>
                                <span className="font-semibold text-[hsl(var(--zt-teal))] capitalize">{h.action}</span>
                                {h.note && <span className="text-[hsl(var(--zt-charcoal))]/60"> — {h.note}</span>}
                                <span className="text-[10px] text-[hsl(var(--zt-charcoal))]/40 ml-2">{fmtDate(h.created_at)}</span>
                              </div>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Required docs checklist */}
        <div className="bg-white rounded-2xl border border-[hsl(var(--zt-beige-deep))] p-6 shadow-sm">
          <h3 className="font-heading uppercase tracking-[0.18em] text-[11px] text-[hsl(var(--zt-teal))]/70 font-bold mb-4">Document Checklist</h3>
          <div className="grid sm:grid-cols-2 gap-2">
            {DOCUMENT_TYPES.map(t => {
              const uploaded = docs.find(d => d.document_type === t.value);
              return (
                <div key={t.value} className="flex items-center gap-2.5 text-sm">
                  {uploaded ? (
                    uploaded.status === "approved"
                      ? <CheckCircle2 size={15} className="text-emerald-500 flex-shrink-0" />
                      : uploaded.status === "rejected"
                        ? <XCircle size={15} className="text-red-400 flex-shrink-0" />
                        : <Clock size={15} className="text-amber-500 flex-shrink-0" />
                  ) : (
                    <div className="w-3.5 h-3.5 rounded-full border-2 border-[hsl(var(--zt-charcoal))]/20 flex-shrink-0" />
                  )}
                  <span className={uploaded ? "text-[hsl(var(--zt-charcoal))]/80" : "text-[hsl(var(--zt-charcoal))]/45"}>
                    {t.label}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

function formatSize(bytes) {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function fmtDate(d) {
  if (!d) return "";
  return new Date(d).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
}
